import React, { useState } from "react";
import { Link } from "react-router-dom";

// ── Tech Radar data ──────────────────────────────────────────────────────────
// Rings: Adopt · Trial · Assess · Hold
// Quadrants: AI & ML · Cloud & Infrastructure · Frontend & DX · Automation & Tooling

type Ring = "Adopt" | "Trial" | "Assess" | "Hold";

interface RadarItem {
  name: string;
  ring: Ring;
  description: string;
  isNew?: boolean;
}

interface Quadrant {
  id: string;
  label: string;
  icon: string;
  color: string;
  bg: string;
  items: RadarItem[];
}

const RING_CONFIG: Record<Ring, { color: string; dot: string; label: string }> = {
  Adopt:  { color: "text-emerald-700", dot: "bg-emerald-500", label: "Adopt" },
  Trial:  { color: "text-blue-700",    dot: "bg-blue-500",    label: "Trial" },
  Assess: { color: "text-amber-700",   dot: "bg-amber-500",   label: "Assess" },
  Hold:   { color: "text-rose-700",    dot: "bg-rose-500",    label: "Hold" },
};

const QUADRANTS: Quadrant[] = [
  {
    id: "ai-ml",
    label: "AI & Machine Learning",
    icon: "🤖",
    color: "text-purple-700",
    bg: "bg-purple-50 border-purple-200",
    items: [
      { name: "Retrieval-Augmented Generation (RAG)", ring: "Adopt", description: "Ground LLM responses in your own data. Production-ready pattern for enterprise knowledge bases and support automation.", isNew: false },
      { name: "GPT-4o / Claude 3.5 Sonnet", ring: "Adopt", description: "Frontier multimodal models suitable for production workloads. Evaluate both for cost-performance trade-offs.", isNew: false },
      { name: "LangChain / LlamaIndex", ring: "Adopt", description: "Mature orchestration frameworks for building agentic pipelines, document Q&A, and tool-calling workflows.", isNew: false },
      { name: "AI Agents & Autonomous Workflows", ring: "Trial", description: "Tool-use loops (ReAct, function-calling) enabling software agents to plan and execute multi-step tasks.", isNew: true },
      { name: "Vector Databases (Pinecone, Weaviate)", ring: "Adopt", description: "Semantic search and similarity retrieval at scale. Essential infrastructure for any RAG architecture.", isNew: false },
      { name: "Fine-tuning Open Models (Llama 3, Mistral)", ring: "Trial", description: "Domain-specific fine-tunes outperform generic models on narrow tasks while reducing latency and cost.", isNew: true },
      { name: "Prompt Engineering Patterns", ring: "Adopt", description: "Chain-of-thought, few-shot, and structured output prompts are now baseline skills for AI product teams.", isNew: false },
      { name: "AI Observability (LangSmith, Langfuse)", ring: "Trial", description: "Trace, eval, and monitor LLM calls in production. Critical for reliability and cost governance.", isNew: true },
      { name: "Multimodal AI (Vision + Text)", ring: "Trial", description: "GPT-4o, Gemini 1.5 Pro enable image understanding, OCR, and document parsing without custom pipelines.", isNew: false },
      { name: "On-Device Inference (WebLLM, Ollama)", ring: "Assess", description: "Run small models in-browser or locally for privacy-sensitive use cases. Still limited by model size.", isNew: true },
      { name: "AI Code Generation (Copilot, Cursor)", ring: "Adopt", description: "Developer productivity multiplier. Integrating AI pair-programming into every engineering workflow is table stakes.", isNew: false },
      { name: "Synthetic Data Generation", ring: "Assess", description: "Useful for augmenting training data and testing edge cases, but quality validation remains a challenge.", isNew: false },
    ],
  },
  {
    id: "cloud",
    label: "Cloud & Infrastructure",
    icon: "☁️",
    color: "text-sky-700",
    bg: "bg-sky-50 border-sky-200",
    items: [
      { name: "Cloudflare Workers (Edge Compute)", ring: "Adopt", description: "Deploy globally distributed serverless functions with sub-millisecond cold starts. Our default deployment target.", isNew: false },
      { name: "Cloudflare D1 (Edge SQL)", ring: "Adopt", description: "SQLite at the edge. Ideal for read-heavy workloads co-located with Workers compute.", isNew: false },
      { name: "Cloudflare R2 (Object Storage)", ring: "Adopt", description: "S3-compatible, zero egress cost. Default object storage for new projects.", isNew: false },
      { name: "Container-first Architectures (Docker, Kubernetes)", ring: "Adopt", description: "Still the gold standard for complex, stateful services. Pair with managed Kubernetes (GKE, EKS) to reduce ops overhead.", isNew: false },
      { name: "Platform Engineering & Internal Developer Portals", ring: "Trial", description: "Backstage and similar IDPs are emerging as the right abstraction for large engineering orgs to manage cloud complexity.", isNew: true },
      { name: "FinOps Practices", ring: "Trial", description: "Cloud cost visibility and chargeback tooling (Infracost, CloudHealth) are becoming essential as cloud bills grow.", isNew: false },
      { name: "WebAssembly on the Edge (WASM)", ring: "Assess", description: "Sandboxed, near-native workloads at the edge. Maturing rapidly; keep an eye on WASI and component model.", isNew: true },
      { name: "Confidential Computing (TEE)", ring: "Assess", description: "Hardware-isolated execution for sensitive data workloads. Relevant for regulated industries.", isNew: false },
      { name: "eBPF for Observability & Security", ring: "Trial", description: "Kernel-level observability without code changes. Cilium and Tetragon are production-ready.", isNew: false },
      { name: "IPv6-only Infrastructure", ring: "Assess", description: "Cloudflare and AWS are nudging workloads toward IPv6. Evaluate readiness of your stack.", isNew: false },
    ],
  },
  {
    id: "frontend",
    label: "Frontend & Developer Experience",
    icon: "⚛️",
    color: "text-indigo-700",
    bg: "bg-indigo-50 border-indigo-200",
    items: [
      { name: "React 18 + Server Components", ring: "Adopt", description: "RSC enables server-side rendering with fine-grained client interactivity. Essential for performance-critical apps.", isNew: false },
      { name: "Next.js 14 (App Router)", ring: "Adopt", description: "Production standard for React applications. App Router + Server Actions streamline full-stack development.", isNew: false },
      { name: "Vite + esbuild", ring: "Adopt", description: "Sub-second HMR and blazing build times. Replace Webpack in all new projects.", isNew: false },
      { name: "TypeScript (Strict Mode)", ring: "Adopt", description: "Non-negotiable for maintainable codebases. Strict mode catches entire classes of runtime errors at compile time.", isNew: false },
      { name: "Tailwind CSS v3", ring: "Adopt", description: "Utility-first CSS at scale. Component libraries like shadcn/ui build on it for a complete design system.", isNew: false },
      { name: "shadcn/ui", ring: "Trial", description: "Copy-paste component library built on Radix UI + Tailwind. Avoids vendor lock-in with full ownership of component code.", isNew: true },
      { name: "Astro (Content Sites)", ring: "Trial", description: "Islands architecture delivers near-zero JS bundles for content-heavy sites. Excellent CWV scores out of the box.", isNew: false },
      { name: "React Native (Expo)", ring: "Trial", description: "Expo SDK 50 + the new architecture (Fabric) make cross-platform mobile viable with a web team.", isNew: false },
      { name: "Adobe Experience Manager (AEM)", ring: "Adopt", description: "Enterprise CMS of choice for large content operations. AEM Edge Delivery Services modernises delivery with GitHub-based authoring.", isNew: false },
      { name: "WordPress + Headless (WPGraphQL)", ring: "Adopt", description: "Headless WordPress remains a pragmatic choice for content teams with existing workflows.", isNew: false },
      { name: "Web Performance Budgets & CWV", ring: "Adopt", description: "LCP < 2.5s, CLS < 0.1, INP < 200ms are now SEO ranking signals. Automate with Lighthouse CI.", isNew: false },
      { name: "Module Federation (Micro-frontends)", ring: "Assess", description: "Viable for large orgs with independent deployment needs. Significant operational complexity — evaluate carefully.", isNew: false },
    ],
  },
  {
    id: "automation",
    label: "Automation & Tooling",
    icon: "⚙️",
    color: "text-orange-700",
    bg: "bg-orange-50 border-orange-200",
    items: [
      { name: "GitHub Actions", ring: "Adopt", description: "Default CI/CD for most teams. Rich marketplace, tight GitHub integration, and reasonable pricing.", isNew: false },
      { name: "Playwright (E2E Testing)", ring: "Adopt", description: "Cross-browser, reliable, and fast. Replaces Cypress for most new projects.", isNew: false },
      { name: "Vitest (Unit Testing)", ring: "Adopt", description: "Vite-native test runner with Jest compatibility. Significantly faster than Jest for modern toolchains.", isNew: false },
      { name: "Jira + Confluence (Atlassian Suite)", ring: "Adopt", description: "Still the dominant project management and documentation platform for enterprise teams.", isNew: false },
      { name: "Google Analytics 4 (GA4)", ring: "Adopt", description: "Post-UA migration is complete. GA4 event model and BigQuery integration are now mature.", isNew: false },
      { name: "Stripe Billing & Payments", ring: "Adopt", description: "Payment infrastructure of choice. Stripe Connect, Billing, and Radar cover the full commerce stack.", isNew: false },
      { name: "n8n / Zapier (No-code Automation)", ring: "Trial", description: "Workflow automation without code. n8n's self-hosted model is attractive for sensitive data pipelines.", isNew: true },
      { name: "Terraform / OpenTofu (IaC)", ring: "Adopt", description: "Infrastructure as Code is mandatory for reproducible cloud environments. OpenTofu is the FOSS Terraform fork.", isNew: false },
      { name: "OpenTelemetry", ring: "Adopt", description: "Vendor-neutral observability standard. Instrument once, export to any backend (Grafana, Datadog, Honeycomb).", isNew: false },
      { name: "AI-assisted Code Review (CodeRabbit, Graphite)", ring: "Trial", description: "Automated PR summaries, security flags, and style suggestions. Reduces review cycle time measurably.", isNew: true },
      { name: "Dependabot / Renovate", ring: "Adopt", description: "Automated dependency updates catch CVEs before they reach production. Enable on every repository.", isNew: false },
      { name: "LLM-powered Test Generation", ring: "Assess", description: "Tools like Codium AI generate unit tests from source code. Quality is improving but still needs human review.", isNew: true },
    ],
  },
];

const RING_ORDER: Ring[] = ["Adopt", "Trial", "Assess", "Hold"];

// ── Component ────────────────────────────────────────────────────────────────

export default function AIRadar() {
  const [activeQuadrant, setActiveQuadrant] = useState<string>("ai-ml");
  const [activeRing, setActiveRing] = useState<Ring | "All">("All");
  const [search, setSearch] = useState("");

  const quadrant = QUADRANTS.find(q => q.id === activeQuadrant)!;

  const filtered = quadrant.items.filter(item => {
    const matchesRing = activeRing === "All" || item.ring === activeRing;
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.description.toLowerCase().includes(search.toLowerCase());
    return matchesRing && matchesSearch;
  });

  const grouped = RING_ORDER.reduce<Record<Ring, RadarItem[]>>((acc, ring) => {
    acc[ring] = filtered.filter(i => i.ring === ring);
    return acc;
  }, { Adopt: [], Trial: [], Assess: [], Hold: [] });

  return (
    <main className="min-h-screen bg-white">

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-slate-900 via-indigo-950 to-purple-950 text-white pt-24 pb-20 px-4">
        <div className="max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5 text-xs font-semibold tracking-wider uppercase mb-6 backdrop-blur">
            <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
            April 2026 Edition
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6">
            Technology <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-purple-400">Radar</span>
          </h1>
          <p className="text-lg sm:text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed mb-8">
            Our opinionated guide to the technologies worth your attention — across AI, cloud, frontend, and automation. 
            Updated quarterly by our engineering and consulting teams.
          </p>
          {/* Ring legend */}
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            {Object.entries(RING_CONFIG).map(([ring, cfg]) => (
              <div key={ring} className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot}`} />
                <span className="text-slate-300 font-medium">{ring}</span>
                <span className="text-slate-500 text-xs">
                  {ring === "Adopt" && "— use in production"}
                  {ring === "Trial" && "— worth piloting"}
                  {ring === "Assess" && "— watch & experiment"}
                  {ring === "Hold" && "— pause new adoption"}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Quadrant tabs ── */}
      <div className="sticky top-[84px] z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex gap-1 overflow-x-auto py-2 no-scrollbar">
            {QUADRANTS.map(q => (
              <button
                key={q.id}
                onClick={() => { setActiveQuadrant(q.id); setActiveRing("All"); setSearch(""); }}
                className={`flex items-center gap-2 whitespace-nowrap px-4 py-2.5 rounded-lg text-sm font-semibold transition shrink-0 ${
                  activeQuadrant === q.id
                    ? "bg-indigo-600 text-white shadow"
                    : "text-gray-600 hover:bg-gray-100"
                }`}
              >
                <span>{q.icon}</span>
                <span>{q.label}</span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── Filter bar ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 flex flex-wrap gap-3 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="text"
            placeholder="Search technologies…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
          />
        </div>
        <div className="flex gap-2 flex-wrap">
          {(["All", ...RING_ORDER] as (Ring | "All")[]).map(ring => (
            <button
              key={ring}
              onClick={() => setActiveRing(ring)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                activeRing === ring
                  ? ring === "All"
                    ? "bg-slate-800 text-white border-slate-800"
                    : `${RING_CONFIG[ring as Ring].dot} text-white border-transparent bg-opacity-90`
                  : "text-gray-600 border-gray-200 hover:border-gray-400"
              } ${
                activeRing === ring && ring !== "All"
                  ? ring === "Adopt" ? "bg-emerald-500"
                  : ring === "Trial" ? "bg-blue-500"
                  : ring === "Assess" ? "bg-amber-500"
                  : "bg-rose-500"
                  : ""
              }`}
            >
              {ring}
            </button>
          ))}
        </div>
        <p className="text-xs text-gray-400 ml-auto">{filtered.length} items</p>
      </div>

      {/* ── Items grid ── */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pb-20">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-gray-400">No results for "{search}"</div>
        ) : (
          RING_ORDER.map(ring => grouped[ring].length > 0 && (
            <section key={ring} className="mb-10">
              <div className={`flex items-center gap-3 mb-4 pb-2 border-b`}>
                <span className={`w-3 h-3 rounded-full ${RING_CONFIG[ring].dot}`} />
                <h2 className={`text-sm font-bold uppercase tracking-widest ${RING_CONFIG[ring].color}`}>{ring}</h2>
                <span className="text-xs text-gray-400 font-medium">
                  {ring === "Adopt" && "— recommended for production use"}
                  {ring === "Trial" && "— worth a pilot project"}
                  {ring === "Assess" && "— explore, experiment, watch"}
                  {ring === "Hold" && "— caution, pause new adoption"}
                </span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {grouped[ring].map(item => (
                  <div
                    key={item.name}
                    className={`relative p-4 rounded-xl border ${quadrant.bg} hover:shadow-md transition group`}
                  >
                    {item.isNew && (
                      <span className="absolute top-3 right-3 text-[10px] font-bold uppercase tracking-wider bg-indigo-600 text-white rounded-full px-2 py-0.5">
                        New
                      </span>
                    )}
                    <h3 className="font-semibold text-gray-900 pr-10 leading-snug mb-1">{item.name}</h3>
                    <p className="text-xs text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                ))}
              </div>
            </section>
          ))
        )}
      </div>

      {/* ── AI CTA ── */}
      <section className="bg-gradient-to-r from-indigo-600 to-purple-700 text-white py-16 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-black mb-4">Navigating these shifts for your business?</h2>
          <p className="text-indigo-100 text-lg mb-8 max-w-2xl mx-auto">
            Our consulting team helps you cut through the noise — identifying which technologies to adopt now and which to watch.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/ai"
              className="px-8 py-3 bg-white text-indigo-700 font-bold rounded-xl hover:bg-indigo-50 transition shadow-lg"
            >
              Our AI Services →
            </Link>
            <Link
              to="/consulting"
              className="px-8 py-3 border-2 border-white/60 text-white font-bold rounded-xl hover:border-white hover:bg-white/10 transition"
            >
              Talk to an Expert
            </Link>
          </div>
        </div>
      </section>

    </main>
  );
}
